import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IMAGES } from '../constants/images';
import styles from './ImpactPage.module.css';
import SEO, { BUSINESS_SCHEMA } from '../components/SEO';

const HOME_SEO_TITLE = 'Movement Green | VEU Accredited Provider — Heat Pump, AC & LED Rebates Victoria';
const HOME_SEO_DESC  = 'VEU Accredited Provider. Access government rebates on heat pump hot water, reverse-cycle air conditioning, LED lighting & commercial solar under Victoria\'s VEU program. Free home assessment across Victoria.';
const HOME_KEYWORDS  = 'Victorian Energy Upgrades, VEU accredited provider, VEU accredited person Victoria, VEU rebates Victoria, heat pump rebate Victoria, reverse cycle air conditioner rebate Victoria, LED lighting upgrade VEU, commercial solar VEU Victoria, government rebate energy Victoria, air conditioner rebate Melbourne, energy upgrade Melbourne';

const HOME_FAQ = [
  {
    q: 'What is the Victorian Energy Upgrades (VEU) program?',
    a: 'The Victorian Energy Upgrades (VEU) program* is a Victorian Government initiative that provides discounts on energy-efficient appliances including heat pumps, reverse-cycle air conditioners, LED lighting, and more. The program is administered by the Essential Services Commission (ESC) and helps Victorian households reduce energy bills and emissions.',
  },
  {
    q: 'How do I access VEU rebates for my Victorian home?',
    a: 'To access VEU benefits, simply book a free home energy assessment with Movement Green. Our team visits your property, identifies every upgrade your home qualifies for, and manages the entire upgrade process — from recommending eligible products to installation and the scheme paperwork, which we handle directly as a VEU Accredited Provider. There is no paperwork for you to handle.',
  },
  {
    q: 'Who is eligible for Victorian Energy Upgrades?',
    a: 'Victorian homeowners, renters (with landlord consent), and businesses across Victoria are eligible for VEU rebates. Eligibility depends on the type of existing appliance and the upgrade being installed. Heat pump hot water, reverse-cycle AC, and LED lighting upgrades are available to most Victorian households. Book a free assessment to confirm your eligibility.',
  },
  {
    q: 'How much can I save with VEU rebates in Victoria?',
    a: 'Savings vary by upgrade. A reverse-cycle air conditioner under the VEU program* can attract a benefit of up to $7,000 depending on VEEC prices at the time. Heat pump hot water systems can reduce hot water bills by up to 70%. LED lighting upgrades save up to 80% on lighting energy costs. Most upgrades also deliver ongoing bill savings every year.',
  },
  {
    q: 'Is there any cost to me for a VEU upgrade?',
    a: 'The initial home or business energy assessment with Movement Green is completely free. Commercial lighting VEU-eligible upgrades are offered at significantly reduced cost — or even at no out-of-pocket cost — thanks to the VEU program.* The VEU program requires that you make a minimum co-payment when upgrading VEU-eligible hot water systems and air conditioning. Final pricing depends on the specific upgrade and your property. Contact us for a no-obligation quote.',
  },
];

const HOME_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      ...BUSINESS_SCHEMA,
      '@id': 'https://movementgreen.com.au/#business',
    },
    {
      '@type': 'WebSite',
      '@id':   'https://movementgreen.com.au/#website',
      url:     'https://movementgreen.com.au',
      name:    'Movement Green',
      description: HOME_SEO_DESC,
      publisher: { '@id': 'https://movementgreen.com.au/#business' },
    },
    {
      '@type': 'FAQPage',
      '@id':   'https://movementgreen.com.au/#faq',
      mainEntity: HOME_FAQ.map(({ q, a }) => ({
        '@type':          'Question',
        name:             q,
        acceptedAnswer:   { '@type': 'Answer', text: a },
      })),
    },
  ],
};

const PROJECTS = [
  { region: 'Metro Melbourne', desc: 'Heat pump hot water, reverse-cycle AC, and LED lighting upgrades across Melbourne suburbs under the VEU program.*' },
  { region: 'Regional Victoria', desc: 'Air conditioning and hot water system upgrades for regional homeowners, helping families access government rebates.*' },
  { region: 'Commercial & Rental', desc: 'VEU-compliant LED lighting and HVAC upgrades for landlords, businesses, and property managers across Victoria.' },
];

function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className={styles.faqList}>
      {items.map((item, i) => (
        <div key={i} className={`${styles.faqItem} ${open === i ? styles.faqItemOpen : ''}`}>
          <button
            className={styles.faqQ}
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span>{item.q}</span>
            <svg className={styles.faqIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className={styles.faqA} aria-hidden={open !== i}>
            <p>{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ImpactPage() {
  return (
    <main className="page-enter">
      <SEO
        title={HOME_SEO_TITLE}
        description={HOME_SEO_DESC}
        keywords={HOME_KEYWORDS}
        schema={HOME_SCHEMA}
      />

      {/* ── HERO — Misty forest full bleed ───────────────── */}
      <section className={styles.hero}>
        <img src={IMAGES.heroBackground} alt="" className={styles.heroBg} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <span className="eyebrow-light">VEU Accredited Provider</span>
            <h1 className={`disp-hero ${styles.heroH1}`}>
              Upgrade.<br/><em>Save.</em><br/>Repeat.
            </h1>
            <p className={styles.heroBody}>
              As a VEU Accredited Provider, we help Victorian homeowners replace old, inefficient appliances with
              energy-efficient alternatives — reducing bills and accessing government
              rebates under the Victorian Energy Upgrades program.<sup>*</sup>
            </p>
            <div className={styles.heroBtns}>
              <Link to="/solutions" className="btn btn-accent btn-lg">See Our Upgrades</Link>
              <Link to="/about" className="btn btn-ghost-light btn-lg">How It Works</Link>
            </div>
          </div>

        </div>
      </section>


      {/* ── PROJECTS — Amazon rainforest photo ─────────── */}
      <section className={styles.projectsSection}>
        <div className={styles.projectsWrap}>
          <div className={styles.projectsLeft}>
            <span className="eyebrow">Victorian Footprint</span>
            <h2 className={`disp-lg ${styles.projectsTitle}`}>Upgrades<br/>Across<br/><em>Victoria.</em></h2>
            <p className={`body-md ${styles.projectsBody}`}>
              From inner-city Melbourne to regional Victoria, we deliver energy-efficient
              upgrades that cut household bills and qualify for government rebates under
              the Victorian Energy Upgrades program.<sup>*</sup>
            </p>
            <div className={styles.projectsList}>
              {PROJECTS.map(p => (
                <div key={p.region} className={styles.projectItem}>
                  <div className={styles.projectItemTop}>
                    <span className={styles.projectRegion}>{p.region}</span>
                  </div>
                  <p className={styles.projectDesc}>{p.desc}</p>
                </div>
              ))}
            </div>
            <Link to="/solutions" className="btn btn-primary btn-lg" style={{marginTop:8}}>
              View Solutions →
            </Link>
          </div>

          <div className={styles.projectsRight}>
            <img src={IMAGES.projectPhoto} alt="Amazon rainforest aerial" className={styles.projectImg}/>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────── */}
      <section className={styles.faqSection}>
        <div className={styles.faqWrap}>
          <div className={styles.faqHead}>
            <span className="eyebrow">Common Questions</span>
            <h2 className={`disp-md ${styles.faqTitle}`}>
              Everything You Need to Know<br/><em>About VEU Rebates.</em>
            </h2>
          </div>
          <FaqAccordion items={HOME_FAQ} />
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaWrap}>
          <div className={styles.ctaInner}>
            <div className={styles.ctaGlow}/>
            <div className={styles.ctaText}>
              <span className="eyebrow-light">Government Rebates Available Now</span>
              <h2 className={`disp-md ${styles.ctaTitle}`}>
                Access thousands in rebates<br/>
                <em>on your next upgrade.</em>
              </h2>
              <p className={styles.ctaBody}>
                Victorian homeowners can access significant government rebates<sup>*</sup> on heat pumps,
                reverse-cycle AC, LED lighting, and more. Book a free assessment to find out what you qualify for.
              </p>
            </div>
            <div className={styles.ctaBtns}>
              <Link to="/contact" className="btn btn-accent btn-lg">Book Free Assessment →</Link>
              <Link to="/solutions" className="btn btn-ghost-light">See Upgrades</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
